import { useEffect, useMemo, useState } from 'react';
import { Checkbox, Modal } from 'antd';
import styled from 'styled-components';

import type { IModelInstance } from '../../../ui-builder';
import type { EpiComposition, EpiIdmpLink } from '../types/types';
import { visibleIdmp } from '../utils/schemaUtils';
import { resolveInstanceName } from '../utils/epiHelpers';
import { useEpiContext } from '../context/EpiContext';

interface IProps {
  isOpen: boolean;
  /**
   * Whether the new composition keeps the source's section structure
   * (`duplicate`) or starts as the empty required-key skeleton (`fresh`).
   * Only affects the modal copy — the IDMP selection mechanic is identical.
   */
  mode: 'duplicate' | 'fresh';
  /** The composition the IDMPs are being peeled off of. */
  source: EpiComposition;
  /** Resolved IDMP model instances for label display. */
  idmpById: Record<string, IModelInstance>;
  onConfirm: (peeledIdmpUuids: string[]) => void;
  onCancel: () => void;
}

const PeelIdmpsModal: React.FC<IProps> = ({
  isOpen,
  mode,
  source,
  idmpById,
  onConfirm,
  onCancel,
}) => {
  const { lang } = useEpiContext();

  const sourceIdmp: EpiIdmpLink[] = useMemo(
    () => visibleIdmp(source.medicinalProducts),
    [source.medicinalProducts]
  );

  const [selected, setSelected] = useState<string[]>([]);

  // Reset whenever the modal opens, so a previous invocation doesn't leak in.
  useEffect(() => {
    if (isOpen) setSelected([]);
  }, [isOpen]);

  const options = useMemo(
    () =>
      sourceIdmp.map((ref, i) => ({
        value: ref.id,
        label:
          resolveInstanceName(idmpById[ref.id]?.data?.name, lang) ??
          `Medicinal product ${i + 1}`,
      })),
    [sourceIdmp, idmpById, lang]
  );

  // Validation: at least one must be picked, AND at least one must stay on
  // source. Otherwise the source's composition.medicinalProducts goes empty and we
  // violate the composition's invariant.
  const sourceVisibleCount = sourceIdmp.length;
  const peelCount = selected.length;
  const tooFew = peelCount === 0;
  const tooMany = peelCount >= sourceVisibleCount;
  const valid = !tooFew && !tooMany;

  const title =
    mode === 'duplicate' ? 'Duplicate document' : 'Add new document';

  const description =
    mode === 'duplicate'
      ? 'Choose the medicinal products to move into a new document. It takes over the current content for them.'
      : 'Choose the medicinal products to move into a new document. It starts from empty sections.';

  return (
    <Modal
      open={isOpen}
      title={title}
      okText="Confirm"
      cancelText="Cancel"
      okButtonProps={{ disabled: !valid }}
      onOk={() => onConfirm(selected)}
      onCancel={onCancel}
      destroyOnHidden
    >
      <Description>{description}</Description>
      <VerticalCheckboxGroup
        options={options}
        value={selected}
        onChange={vals => setSelected(vals as string[])}
      />
      {tooFew && <Hint>Select at least one medicinal product.</Hint>}
      {tooMany && (
        <Hint>
          At least one medicinal product has to stay in the source document.
        </Hint>
      )}
    </Modal>
  );
};

export default PeelIdmpsModal;

const Description = styled.p`
  margin: 0 0 12px;
  font-size: 13px;
  color: #4b576a;
`;

const Hint = styled.div`
  margin-top: 8px;
  font-size: 12px;
  color: #ad6800;
`;

const VerticalCheckboxGroup = styled(Checkbox.Group)`
  display: flex;
  flex-direction: column;
  gap: 8px;

  .ant-checkbox-wrapper {
    margin-inline-start: 0;
  }
`;
